import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProjects } from '../api/client'
import type { Project } from '../types'
import { useUIStore } from '../store/uiStore'
import NewProjectModal from '../components/NewProjectModal'

export default function ProjectsPage() {
  const navigate = useNavigate()
  const openModal = useUIStore((s) => s.openModal)
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const load = async () => {
    setIsLoading(true)
    try {
      const rows = await getProjects()
      setProjects(rows)
    } catch (e) {
      console.error('Failed to load projects:', e)
      alert(`Failed to load projects: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif font-semibold text-2xl sm:text-3xl text-ink">Projects</h1>
            <div className="mt-1 text-sm text-muted max-w-prose">
              Group related forecasts together and track how probabilities change over time.
            </div>
          </div>
          <button
            className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] transition"
            type="button"
            onClick={() => openModal('newProject')}
          >
            ＋ New project
          </button>
        </div>

        {isLoading ? (
          <div className="mt-4 font-mono text-xs text-muted">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 text-sm text-muted">
            No projects yet.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {projects.map((p) => (
              <button
                key={p.project_id}
                type="button"
                className="text-left rounded-delphi border border-rule bg-paper2 shadow-delphi px-4 py-3 hover:bg-white/30 transition"
                onClick={() => navigate(`/projects/${p.project_id}`)}
              >
                <div className="font-serif font-semibold text-ink">{p.name}</div>
                <div className="mt-1 text-sm text-muted line-clamp-3">{p.description || 'No description.'}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="font-mono text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                    {p.question_count ?? 0} questions
                  </span>
                  <span className="font-mono text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                    {p.run_count ?? 0} runs
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <NewProjectModal onCreated={load} />
    </div>
  )
}

